import { z } from 'zod';
/**
 * Password policy per AC-AUTH-03.3: each unmet rule must be nameable, so each
 * rule is its own refinement with its own message.
 */
export const PasswordSchema = z
    .string()
    .min(8, 'at least 8 characters')
    .regex(/[A-Z]/, 'at least one uppercase letter')
    .regex(/[a-z]/, 'at least one lowercase letter')
    .regex(/\d/, 'at least one digit');
/** E.164 with country code (docs/03 §1.1). */
export const E164Phone = z.string().regex(/^\+[1-9]\d{6,14}$/, 'E.164 phone number required');
export const EmailSignupInput = z.object({
    email: z.string().email().toLowerCase(),
    password: PasswordSchema,
});
export const EmailLoginInput = z.object({
    email: z.string().email().toLowerCase(),
    password: z.string().min(1),
});
/** Server-enforced email login lock, shared with the mobile countdown. */
export const EmailLoginLocked = z.object({
    code: z.literal('LOGIN_LOCKED'),
    message: z.string(),
    lockedUntil: z.iso.datetime(),
    retryAfterSeconds: z.number().int().min(1).max(900),
});
/** Change Password (docs/03 §1.2): current must verify, new ≠ current. */
export const ChangePasswordInput = z
    .object({ currentPassword: z.string().min(1), newPassword: PasswordSchema })
    .refine((v) => v.currentPassword !== v.newPassword, {
    message: 'New password must differ from the current one',
    path: ['newPassword'],
});
/**
 * Forgot Password, in three steps:
 *
 *   forgot-password  →  email carries a 6-digit code
 *   verify-reset-otp →  code is spent for a short-lived reset token
 *   reset-password   →  token is spent for the new password
 *
 * The code and the token are deliberately DIFFERENT credentials. A six-digit
 * code is guessable — a million values, and the member is typing it — so it
 * carries an attempt cap and a lockout and buys only one thing: the right to
 * receive the token. The token is 256 bits, so the endpoint that actually
 * writes a password is not defending against guesses at all.
 *
 * Collapsing the two — letting the code itself authorise the write — would put
 * a six-digit secret in front of the password change, which is the shape this
 * split exists to avoid.
 *
 * All three are separate from `ChangePasswordInput`: that one belongs to a
 * signed-in member proving they know their current password, these belong to
 * somebody who cannot sign in at all.
 */
export const ForgotPasswordInput = z.object({ email: z.string().email().toLowerCase() });
/**
 * The SAME answer whether or not the address has an account.
 *
 * A response that differed — 404, a distinct message, even a distinguishable
 * latency — turns this endpoint into a membership oracle: anyone could walk a
 * list of addresses and learn which ones are registered here. That matters
 * more than usual for a civic network, where holding an account is itself
 * something a member may not want disclosed.
 *
 * The wording is conditional on purpose, and the app should not improve it
 * into "sent": a member who mistypes their address must be told the same thing
 * as one who did not.
 */
export const PasswordResetRequested = z.object({ message: z.string() });
/** Step two. Six digits, as sent — not four, and never a phone OTP. */
export const VerifyResetOtpInput = z.object({
    email: z.string().email().toLowerCase(),
    otp: z.string().regex(/^\d{6}$/, '6 digit code'),
});
/**
 * The reward for a correct code: a short-lived token that authorises exactly
 * one password write and nothing else.
 *
 * It is NOT a session. It cannot be sent as a bearer token, it names no
 * account to the client, and it is spent by `resetPassword` alone — so a leak
 * costs the password of one account whose mailbox the holder already reached,
 * rather than access to the account itself.
 */
export const ResetOtpVerified = z.object({
    message: z.string(),
    resetToken: z.string(),
});
/**
 * Step three. `newPassword` is the same `PasswordSchema` every other password
 * write uses, so the rules cannot drift between the ways a password can be set.
 */
export const ResetPasswordInput = z.object({
    /** From `verify-reset-otp`; never the account id, the email or the code. */
    resetToken: z.string().min(16).max(512),
    newPassword: PasswordSchema,
});
export const PasswordReset = z.object({ message: z.string() });
export const OtpStartInput = z.object({ phone: E164Phone });
/** OTP: 4–6 digits (AC-AUTH-04). */
export const OtpVerifyInput = z.object({
    phone: E164Phone,
    code: z.string().regex(/^\d{4,6}$/, '4–6 digit code'),
});
export const AuthTokens = z.object({
    accessToken: z.string(),
    tokenType: z.literal('Bearer'),
    /** Access token lifetime in seconds — 15 minutes (`JWT_EXPIRES_IN`). */
    expiresIn: z.number().int().positive(),
    /**
     * Exchange this for a fresh access token at `POST /auth/refresh` when the
     * one above expires, instead of asking the member to sign in again.
     *
     * OPTIONAL on the wire, which is what makes it additive: a client written
     * before it existed ignores the extra key and keeps signing in every fifteen
     * minutes exactly as it does today, and a client written after it still
     * parses a response from an API that predates it. Nothing about the access
     * token changed.
     *
     * It is NOT a bearer token: it authenticates nothing, cannot be sent in an
     * Authorization header, and buys only one thing — a new pair from the
     * refresh endpoint.
     */
    refreshToken: z.string().optional(),
    /** Refresh token lifetime in seconds. Absent when no refresh token is issued. */
    refreshExpiresIn: z.number().int().positive().optional(),
});
/**
 * Trade a refresh token for a new pair (`POST /auth/refresh`).
 *
 * Unauthenticated by nature: the caller's access token has expired, which is
 * the whole reason they are here. The refresh token IS the credential.
 */
export const RefreshTokenInput = z.object({
    refreshToken: z.string().min(16).max(512),
});
/**
 * Deliberately carries no code, in any environment.
 *
 * While the SMS gateway is outstanding (docs/06 §9) a deployment lists specific
 * test numbers through `OTP_TEST_NUMBERS` instead — the tester already holds
 * the value, so returning it would add nothing and would mean the endpoint
 * hands a working credential to anyone who can name a phone number.
 */
export const OtpStarted = z.object({
    /** Resend cooldown surfaced to the UI (AC-AUTH-04.2). */
    resendAfterSeconds: z.number().int().positive(),
});
//# sourceMappingURL=auth.js.map